import React, { useState, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useToast from "@/hooks/useToast";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { ToastLocation } from "@/types/providers/toast";

export type { ToastLocation };

const STORAGE_KEY = "toast_location";
const DEFAULT_LOCATION: ToastLocation = "bottom-right";

const POSITIONS: { row: number; col: number; value: ToastLocation; label: string }[] = [
  { row: 0, col: 0, value: "top-left", label: "Top Left" },
  { row: 0, col: 1, value: "top-center", label: "Top Center" },
  { row: 0, col: 2, value: "top-right", label: "Top Right" },
  { row: 1, col: 0, value: "bottom-left", label: "Bottom Left" },
  { row: 1, col: 1, value: "bottom-center", label: "Bottom Center" },
  { row: 1, col: 2, value: "bottom-right", label: "Bottom Right" },
];

export function ToastLocationPicker() {
  const { colors } = useTheme();
  const { setToastLocation } = useToast();
  const [location, setLocation] = useState<ToastLocation>(DEFAULT_LOCATION);

  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY).then((value) => {
      if (value) {
        setLocation(value as ToastLocation);
      }
    });
  }, []);

  const handleSelect = async (value: ToastLocation) => {
    setLocation(value);
    setToastLocation(value);
    await AsyncStorage.setItem(STORAGE_KEY, value);
  };

  const selectedLabel = POSITIONS.find((p) => p.value === location)?.label || "Bottom Right";

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <ThemedText style={styles.label}>Notification Position</ThemedText>
        <ThemedText style={[styles.selectedLabel, { color: colors.textSecondary }]}>{selectedLabel}</ThemedText>
      </View>
      <View style={[styles.screenPreview, { borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}>
        {/* Mini screen representation */}
        <View style={styles.screenContent}>
          {[0, 1].map((row) => (
            <View key={row} style={[styles.row, row === 0 ? styles.topRow : styles.bottomRow]}>
              {POSITIONS.filter((p) => p.row === row).map((pos) => {
                const isSelected = location === pos.value;
                return (
                  <Pressable
                    key={pos.value}
                    style={[
                      styles.positionZone,
                      pos.col === 0 && styles.leftZone,
                      pos.col === 1 && styles.centerZone,
                      pos.col === 2 && styles.rightZone,
                    ]}
                    onPress={() => handleSelect(pos.value)}
                  >
                    <View
                      style={[
                        styles.notificationPreview,
                        {
                          backgroundColor: isSelected ? colors.primary : colors.border,
                          opacity: isSelected ? 1 : 0.3,
                        },
                      ]}
                    >
                      {isSelected && (
                        <>
                          <View style={[styles.notificationLine, { backgroundColor: colors.buttonText }]} />
                          <View style={[styles.notificationLineShort, { backgroundColor: colors.buttonText }]} />
                        </>
                      )}
                    </View>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
    gap: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
  },
  selectedLabel: {
    fontSize: 12,
  },
  screenPreview: {
    width: "100%",
    aspectRatio: 16 / 10,
    borderRadius: 8,
    borderWidth: 2,
    padding: 8,
  },
  screenContent: {
    flex: 1,
    justifyContent: "space-between",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  topRow: {
    alignItems: "flex-start",
  },
  bottomRow: {
    alignItems: "flex-end",
  },
  positionZone: {
    padding: 4,
  },
  leftZone: {
    alignItems: "flex-start",
  },
  centerZone: {
    alignItems: "center",
  },
  rightZone: {
    alignItems: "flex-end",
  },
  notificationPreview: {
    width: 48,
    height: 20,
    borderRadius: 4,
    padding: 4,
    justifyContent: "center",
    gap: 2,
  },
  notificationLine: {
    height: 2,
    width: "80%",
    borderRadius: 1,
  },
  notificationLineShort: {
    height: 2,
    width: "50%",
    borderRadius: 1,
  },
});
